import { SortingState } from "@tanstack/react-table";

import { CustomFolderFileType, Status } from "src/core/constants/folders";
import { Roles } from "src/core/constants/roles";
import { ElementInputValue, Mission, MissionStatus, User } from "src/core/gql/graphql";
import { getInputValue } from "src/core/utils/folderAnalysis";

/** Compatible avec FolderFilesPageSortingFieldInputType (name: FilesPageSortFieldName, direction: SortingOrder). */
export type QueryVariablesFolderFilesPageSortField = {
  name: "RiskLevel" | "LastModification";
  direction: "ASC" | "DESC";
};

/** Compatible avec InactiveMissionsInLatestSnapshotOfCustomerFoldersPageSortingFieldInputType pour RENEWAL. */
export type QueryVariablesRenewalSortField = {
  name: "RiskLevel" | "RenewDate";
  direction: "ASC" | "DESC";
};

export type QueryVariablesFolderFilesPage = {
  stateIds?: string[];
  fileStateIds?: string[];
  folderFileType?: CustomFolderFileType[];
  limit: number;
  offset: number;
  sort?: QueryVariablesFolderFilesPageSortField[] | QueryVariablesRenewalSortField[];
  ownerUserIdFilter?: string;
  followedByUserIdFilter?: string;
  myFoldersFilter?: boolean;
};

const addOptionalFilters = (
  variables: QueryVariablesFolderFilesPage,
  ownerUserIdFilter: string,
  followedByUserIdFilter: string,
  myFoldersFilter: boolean,
  user: User | null,
  hasRole: (role: Roles) => boolean,
  sorting: SortingState,
  status: string
): QueryVariablesFolderFilesPage => {
  if (myFoldersFilter) {
    if (hasRole(Roles.COLLABORATEUR)) {
      variables.followedByUserIdFilter = user?.id;
    } else {
      variables.ownerUserIdFilter = user?.id;
    }
  } else {
    if (ownerUserIdFilter && ownerUserIdFilter.trim() !== "") {
      variables.ownerUserIdFilter = ownerUserIdFilter;
    }
    if (followedByUserIdFilter && followedByUserIdFilter.trim() !== "") {
      variables.followedByUserIdFilter = followedByUserIdFilter;
    }
  }
  if (sorting.length > 0) {
    const sortColumn = sorting[0];
    const isSortingByRisk = sortColumn.id === "risk";
    const isSortingByUpdatedAt = sortColumn.id === "updatedAt";

    const direction = sortColumn.desc ? "DESC" : "ASC";
    if (
      status === Status.DRAFT ||
      status === Status.VALIDATION_PENDING ||
      status === Status.VALIDATION_PENDING_HR ||
      status === Status.VALIDATED
    ) {
      if (isSortingByRisk) {
        variables.sort = [{ name: "RiskLevel", direction }];
      } else if (isSortingByUpdatedAt) {
        variables.sort = [{ name: "LastModification", direction }];
      }
    } else if (status === Status.RENEWAL) {
      if (isSortingByRisk) {
        variables.sort = [{ name: "RiskLevel", direction }];
      } else if (isSortingByUpdatedAt) {
        variables.sort = [{ name: "RenewDate", direction }];
      }
    }
  }
  return variables;
};

export const buildQueryVariablesFolderFilesPage = (
  value: string,
  sorting: SortingState,
  limit = 50,
  offset = 0,
  ownerUserIdFilter = "",
  followedByUserIdFilter = "",
  myFoldersFilter = false,
  user: User | null = null,
  hasRole: (role: Roles) => boolean = () => false
): QueryVariablesFolderFilesPage => {
  const baseVariables = {
    limit,
    offset,
  };

  switch (value) {
    case Status.DRAFT: {
      return addOptionalFilters(
        {
          ...baseVariables,
          stateIds: [Status.DRAFT],
          folderFileType: [
            CustomFolderFileType.MISSION_CREATION,
            CustomFolderFileType.MISSION_CREATION_PPH,
            CustomFolderFileType.MISSION_RENEWAL,
            CustomFolderFileType.MISSION_RENEWAL_PPH,
          ],
        },
        ownerUserIdFilter,
        followedByUserIdFilter,
        myFoldersFilter,
        user,
        hasRole,
        sorting,
        value
      );
    }
    case Status.RENEWAL: {
      return addOptionalFilters(
        {
          ...baseVariables,
          stateIds: [Status.DRAFT],
          folderFileType: [
            CustomFolderFileType.MISSION_RENEWAL,
            CustomFolderFileType.MISSION_RENEWAL_PPH,
          ] as CustomFolderFileType[],
        },
        ownerUserIdFilter,
        followedByUserIdFilter,
        myFoldersFilter,
        user,
        hasRole,
        sorting,
        value
      );
    }
    case Status.VALIDATION_PENDING:
    case Status.VALIDATION_PENDING_HR:
      return addOptionalFilters(
        {
          ...baseVariables,
          stateIds: [value],
        },
        ownerUserIdFilter,
        followedByUserIdFilter,
        myFoldersFilter,
        user,
        hasRole,
        sorting,
        value
      );
    case Status.VALIDATED: {
      return addOptionalFilters(
        {
          ...baseVariables,
          fileStateIds: [value],
        },
        ownerUserIdFilter,
        followedByUserIdFilter,
        myFoldersFilter,
        user,
        hasRole,
        sorting,
        value
      );
    }
    default:
      throw new Error(`Invalid status: ${value}`);
  }
};

/**
 * Vérifie si une mission est active en tenant compte de la date de renouvellement
 * et de la date d'aujourd'hui.
 * Normalisation à 00:00:00 local pour comparer les dates.
 */
export const isMissionActive = (mission: Mission): boolean => {
  const renewDate = getInputValue(mission.inputValues as ElementInputValue[], "renewDate");

  if (mission.status !== MissionStatus.Active || renewDate === null) {
    return false;
  }

  const rd = new Date(renewDate);
  if (Number.isNaN(rd.getTime())) return false;

  const today = new Date();

  // normalisation à 00:00:00 local
  rd.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return rd > today;
};
